import React from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Button from '../components/ui/Button';
import { FaLightbulb, FaIndustry, FaGlobe, FaChartLine, FaTools, FaBolt } from 'react-icons/fa';
import { IconWrapper } from '../components/icons/IconWrapper';

const Hero = styled.section`
  position: relative;
  height: 80vh;
  min-height: 500px;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.light};
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 100%;
    height: 200px;
    background: linear-gradient(to top, ${({ theme }) => theme.colors.darkest}, transparent);
    z-index: 1;
  }
`;

const BackgroundVideoContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  z-index: 2;
  text-align: center;
  
  h1 {
    font-size: 3.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    line-height: 1.2;
    color: ${({ theme }) => theme.colors.light};
    
    span {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
  
  p {
    font-size: 1.25rem;
    margin-bottom: 2rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    h1 {
      font-size: 2.5rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
    max-width: 300px;
    margin: 0 auto;
  }
`;

const Section = styled.section`
  padding: 5rem 0;
  background-color: ${({ theme }) => theme.colors.darkest};
  color: ${({ theme }) => theme.colors.light};
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 3rem;
  font-size: 2.5rem;
  position: relative;
  padding-bottom: 1rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(
      to right,
      ${({ theme }) => theme.colors.primary},
      ${({ theme }) => theme.colors.secondary}
    );
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 4rem;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.lightGray};
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled(Link)`
  background-color: ${({ theme }) => theme.colors.darker};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  transition: ${({ theme }) => theme.transitions.medium};
  height: 100%;
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.colors.light};
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${({ theme }) => theme.shadows.large};
    
    .product-image {
      transform: scale(1.05);
    }
  }
`;

const ProductImageContainer = styled.div`
  height: 200px;
  position: relative;
  overflow: hidden;
`;

const ProductImage = styled.div<{ image: string }>`
  width: 100%;
  height: 100%;
  background-image: url(${({ image }) => image});
  background-size: cover;
  background-position: center;
  transition: ${({ theme }) => theme.transitions.medium};
`;

const ProductContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ProductTitle = styled.h3`
  margin-bottom: 0.75rem;
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.light};
`;

const ProductDescription = styled.p`
  color: ${({ theme }) => theme.colors.lightGray};
  margin-bottom: 1.5rem;
  flex: 1;
`;

const ProductLink = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  display: flex;
  align-items: center;
  
  &::after {
    content: '→';
    margin-left: 0.5rem;
    transition: ${({ theme }) => theme.transitions.short};
  }
  
  ${ProductCard}:hover & {
    &::after {
      transform: translateX(5px);
    }
  }
`;

const FeaturesSection = styled(Section)`
  background-color: ${({ theme }) => theme.colors.dark};
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background-color: ${({ theme }) => theme.colors.darker};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: 2rem;
  box-shadow: ${({ theme }) => theme.shadows.small};
  transition: ${({ theme }) => theme.transitions.medium};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
  }
`;

const FeatureIcon = styled.div`
  width: 70px;
  height: 70px;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.primary},
    ${({ theme }) => theme.colors.secondary}
  );
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.light};
  font-size: 2rem;
`;

const FeatureTitle = styled.h3`
  margin-bottom: 1rem;
  font-size: 1.5rem;
`;

const FeatureDescription = styled.p`
  color: ${({ theme }) => theme.colors.lightGray};
`;

const IndustriesSection = styled(Section)`
  background-color: ${({ theme }) => theme.colors.darker};
`;

const IndustriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const IndustryCard = styled(Link)`
  position: relative;
  height: 250px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: ${({ theme }) => theme.colors.light};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1;
    transition: ${({ theme }) => theme.transitions.medium};
  }
  
  &:hover {
    color: ${({ theme }) => theme.colors.light};
    
    &::before {
      background-color: rgba(0, 0, 0, 0.2);
    }
    
    .industry-image {
      transform: scale(1.1);
    }
  }
`;

const IndustryImage = styled.div<{ image: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${({ image }) => image});
  background-size: cover;
  background-position: center;
  transition: ${({ theme }) => theme.transitions.medium};
`;

const IndustryContent = styled.div`
  position: relative;
  z-index: 2;
  padding: 1rem;
`;

const IndustryTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  font-weight: 700;
`;

const IndustryDescription = styled.p`
  font-size: 0.9rem;
  max-width: 250px;
  margin: 0 auto;
  color: ${({ theme }) => theme.colors.light};
`;

const CtaSection = styled.section`
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.primary}, 
    ${({ theme }) => theme.colors.secondary}
  );
  color: ${({ theme }) => theme.colors.light};
  padding: 5rem 0;
  text-align: center;
`;

const CtaContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const CtaTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2rem;
  }
`;

const CtaText = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.1rem;
  }
`;

const ButtonLink = styled(Link)`
  text-decoration: none;
  display: inline-block;
`;

const ViewAllButtonContainer = styled.div`
  text-align: center;
  margin-top: 2rem;
`;

const HomePage: React.FC = () => {
  const pageTitle = 'ElectroCable | Premium Wire & Cable Manufacturer';
  const pageDescription = 'Your leading source for high-quality copper and aluminum wire and cable. ElectroCable offers a wide range of products for residential, commercial, and industrial applications. Get custom quotes and expert solutions.';
  const siteUrl = 'https://www.yourwebsite.com'; // Replace with your actual domain
  const ogImageUrl = `${siteUrl}/images/logo.svg`; // Or a specific homepage OG image

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:site_name" content="ElectroCable" />
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={siteUrl} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:image" content={ogImageUrl} />

        {/* JSON-LD Structured Data for WebSite */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "ElectroCable",
            "url": siteUrl,
            // If you have an internal site search, you can specify it here
            // "potentialAction": {
            //   "@type": "SearchAction",
            //   "target": `${siteUrl}/search?q={search_term_string}`,
            //   "query-input": "required name=search_term_string"
            // }
          })}
        </script>
      </Helmet>

      <Hero>
        <BackgroundVideoContainer>
          <video
            autoPlay
            muted
            loop
            playsInline
            poster="https://media3.giphy.com/media/ulGst81YN6Neo9JFuI/giphy.gif?cid=6c09b952gnrsq90wny3gt8qwn4nvr5p90ygca4r7x5o4dqk6&ep=v1_internal_gif_by_id&rid=giphy.gif&ct=g"
          >
            <source src="https://assets.mixkit.co/videos/preview/mixkit-factory-conveyor-belt-package-processing-4796-large.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </BackgroundVideoContainer>
        <HeroContent>
          <h1>
            Wire & Cable Manufacturer
          </h1>
          <p>
            Industry-leading copper and aluminum wire and cable solutions engineered for reliability and performance.
            Direct-to-customer manufacturing delivers premium quality at competitive prices.
            Get your custom quote within 24 hours and power your projects with confidence.
          </p>
          <ButtonGroup>
            <ButtonLink to="/products">
              <Button variant="outlined" size="large">
                Explore Products
              </Button>
            </ButtonLink>
            <ButtonLink to="/request-quote">
              <Button variant="primary" size="large">
                Get A Quote
              </Button>
            </ButtonLink>
          </ButtonGroup>
        </HeroContent>
      </Hero>

      <Section>
        <SectionTitle>Our Products</SectionTitle>
        <SectionSubtitle>
          ElectroCable offers a comprehensive range of high-quality wire and cable products
          designed for various applications, from residential projects to industrial solutions.
        </SectionSubtitle>

        <ProductGrid>
          <ProductCard to="/products?category=bare-aluminum">
            <ProductImageContainer>
              <ProductImage image="/images/bare-aluminum.jpg" className="product-image" />
            </ProductImageContainer>
            <ProductContent>
              <ProductTitle>Bare Aluminum</ProductTitle>
              <ProductDescription>
                Premium bare aluminum conductors including AAAC-6201 Alloy, AAC, and ACSR
                for overhead transmission and distribution networks.
              </ProductDescription>
              <ProductLink>View Category</ProductLink>
            </ProductContent>
          </ProductCard>

          <ProductCard to="/products?category=service-drop">
            <ProductImageContainer>
              <ProductImage image="/images/service-drop.jpg" className="product-image" />
            </ProductImageContainer>
            <ProductContent>
              <ProductTitle>Service Drop</ProductTitle>
              <ProductDescription>
                Complete range of service drop cables including duplex, triplex, and quadruplex
                configurations for connecting utility power lines to buildings.
              </ProductDescription>
              <ProductLink>View Category</ProductLink>
            </ProductContent>
          </ProductCard>

          <ProductCard to="/products?category=600-volt-ud">
            <ProductImageContainer>
              <ProductImage image="/images/ud-cable.jpg" className="product-image" />
            </ProductImageContainer>
            <ProductContent>
              <ProductTitle>600V UD</ProductTitle>
              <ProductDescription>
                High-performance 600V underground distribution cables available in single,
                duplex, triplex, and quadruplex aluminum configurations.
              </ProductDescription>
              <ProductLink>View Category</ProductLink>
            </ProductContent>
          </ProductCard>
        </ProductGrid>
      </Section>

      <IndustriesSection>
        <SectionTitle>Industries We Serve</SectionTitle>
        <SectionSubtitle>
          Our cable and connector products power critical applications across diverse
          industries, ensuring reliable performance where it matters most.
        </SectionSubtitle>

        <IndustriesGrid>
          <IndustryCard to="/industries">
            <IndustryImage image="https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" className="industry-image" />
            <IndustryContent>
              <IndustryTitle>Residential</IndustryTitle>
              <IndustryDescription>
                Safe and reliable wiring solutions for homes and residential buildings.
              </IndustryDescription>
            </IndustryContent>
          </IndustryCard>

          <IndustryCard to="/industries">
            <IndustryImage image="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" className="industry-image" />
            <IndustryContent>
              <IndustryTitle>Commercial</IndustryTitle>
              <IndustryDescription>
                High-performance wiring for offices, retail spaces, and commercial properties.
              </IndustryDescription>
            </IndustryContent>
          </IndustryCard>

          <IndustryCard to="/industries">
            <IndustryImage image="/images/industrial-factory-worker.jpg" className="industry-image" />
            <IndustryContent>
              <IndustryTitle>Industrial</IndustryTitle>
              <IndustryDescription>
                Durable cables designed for demanding industrial environments and applications.
              </IndustryDescription>
            </IndustryContent>
          </IndustryCard>

          <IndustryCard to="/industries">
            <IndustryImage image="https://images.unsplash.com/photo-1516549655169-df83a0774514?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" className="industry-image" />
            <IndustryContent>
              <IndustryTitle>Healthcare</IndustryTitle>
              <IndustryDescription>
                Specialized wiring solutions for hospitals and healthcare facilities.
              </IndustryDescription>
            </IndustryContent>
          </IndustryCard>

          <IndustryCard to="/industries">
            <IndustryImage image="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" className="industry-image" />
            <IndustryContent>
              <IndustryTitle>Data Centers</IndustryTitle>
              <IndustryDescription>
                High-capacity power and network cabling for data center infrastructure.
              </IndustryDescription>
            </IndustryContent>
          </IndustryCard>

          <IndustryCard to="/industries">
            <IndustryImage image="https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" className="industry-image" />
            <IndustryContent>
              <IndustryTitle>Green Energy</IndustryTitle>
              <IndustryDescription>
                Specialized cables for solar, wind, and other renewable energy applications.
              </IndustryDescription>
            </IndustryContent>
          </IndustryCard>
        </IndustriesGrid>

        <ViewAllButtonContainer>
          <ButtonLink to="/industries">
            <Button variant="outlined" size="large">
              View All Industries
            </Button>
          </ButtonLink>
        </ViewAllButtonContainer>
      </IndustriesSection>

      <FeaturesSection>
        <SectionTitle>Why Choose ElectroCable</SectionTitle>
        <SectionSubtitle>
          We do more than make cables. We solve problems and deliver innovative solutions
          to power your projects with reliability and efficiency.
        </SectionSubtitle>

        <FeatureGrid>
          <FeatureCard>
            <FeatureIcon>
              <IconWrapper icon={FaLightbulb} />
            </FeatureIcon>
            <FeatureTitle>Quality Assurance</FeatureTitle>
            <FeatureDescription>
              Our rigorous quality control process ensures that every product meets
              the highest industry standards for safety and performance.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <IconWrapper icon={FaBolt} />
            </FeatureIcon>
            <FeatureTitle>Low, Medium and High Voltage Solutions</FeatureTitle>
            <FeatureDescription>
              Specialized low, medium and high voltage cables engineered for utility distribution,
              industrial power systems, and renewable energy applications.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <IconWrapper icon={FaGlobe} />
            </FeatureIcon>
            <FeatureTitle>Global Supply Chain</FeatureTitle>
            <FeatureDescription>
              With manufacturing and logistics facilities across multiple continents, we provide quick
              delivery and reliable service to customers worldwide.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <IconWrapper icon={FaIndustry} />
            </FeatureIcon>
            <FeatureTitle>Custom Solutions</FeatureTitle>
            <FeatureDescription>
              Our experienced team can develop custom wire and cable solutions
              tailored to your specific project requirements.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <IconWrapper icon={FaChartLine} />
            </FeatureIcon>
            <FeatureTitle>Competitive Pricing</FeatureTitle>
            <FeatureDescription>
              We offer excellent value with competitive pricing and no hidden fees,
              helping you optimize your project budget.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <IconWrapper icon={FaTools} />
            </FeatureIcon>
            <FeatureTitle>Technical Support</FeatureTitle>
            <FeatureDescription>
              Our knowledgeable technical support team is always ready to assist
              with product selection and application guidance.
            </FeatureDescription>
          </FeatureCard>
        </FeatureGrid>
      </FeaturesSection>

      <CtaSection>
        <CtaContainer>
          <CtaTitle>The Right Wiring Solution Makes All The Difference</CtaTitle>
          <CtaText>
            Choosing the wrong electrical cable can lead to project delays, compliance issues, and safety concerns.
            Our engineering team specializes in selecting the perfect materials for your specific requirements.
            Request a personalized quote today with no obligation.
          </CtaText>
          <ButtonLink to="/request-quote">
            <Button variant="outlined" size="large">
              Request Your Quote
            </Button>
          </ButtonLink>
        </CtaContainer>
      </CtaSection>
    </>
  );
};

export default HomePage; 